import { EXTERNAL_API_VALIDATION_STAMP_KEY } from './externalApiValidationConstants';
import { findFormItemByCustomId, getExternalApiFormValue } from './formUtils';

const md5 = require('md5');

export { EXTERNAL_API_VALIDATION_STAMP_KEY };

/**
 * @param {object} document
 * @returns {Record<string, string[]>|null}
 */
export const getExternalApiValidationConfig = document => {
  const validation = document?.attributes?.externalApi?.validation;
  if (!validation || typeof validation !== 'object') return null;
  return Object.keys(validation).length ? validation : null;
};

/**
 * Payload sent to external-api/validate for one endpoint.
 * @param {Array} formStructure
 * @param {Map|object} formData
 * @param {string[]} customIds
 */
export const buildExternalApiValidationPayload = (
  formStructure,
  formData,
  customIds
) => {
  const data = {};
  (customIds || []).forEach(customId => {
    const item = findFormItemByCustomId(formStructure, customId);
    if (!item) return;
    const value =
      formData instanceof Map ? formData.get(item.id) : formData?.[item.id];
    data[customId] = getExternalApiFormValue(item, value);
  });
  return data;
};

const stableStringify = value => {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

export const hashExternalApiValidationPayload = data =>
  md5(stableStringify(data || {}));

/**
 * @returns {Record<string, string>|null} externalApiId → payload hash
 */
export const computeExternalApiValidationStamp = (
  document,
  formStructure,
  formData
) => {
  const validation = getExternalApiValidationConfig(document);
  if (!validation || !formStructure) return null;

  const stamp = {};
  Object.entries(validation).forEach(([externalApiId, customIds]) => {
    stamp[externalApiId] = hashExternalApiValidationPayload(
      buildExternalApiValidationPayload(formStructure, formData, customIds)
    );
  });
  return stamp;
};

export const getExternalApiValidationStampFromFormData = formData => {
  if (!formData) return null;
  return formData instanceof Map
    ? formData.get(EXTERNAL_API_VALIDATION_STAMP_KEY)
    : formData[EXTERNAL_API_VALIDATION_STAMP_KEY];
};

/**
 * True when document has no external validation, or stamp matches current values.
 */
export const isExternalApiValidationStampValid = (
  document,
  formStructure,
  formData
) => {
  const validation = getExternalApiValidationConfig(document);
  if (!validation) return true;

  const expected = computeExternalApiValidationStamp(
    document,
    formStructure,
    formData
  );
  const actual = getExternalApiValidationStampFromFormData(formData);
  if (!expected || !actual || typeof actual !== 'object') return false;

  return Object.keys(expected).every(
    externalApiId => actual[externalApiId] === expected[externalApiId]
  );
};

/** i18n key for the primary exit action while external validation is still due. */
export const FORMS_EXTERNAL_VALIDATE_TITLE_KEY = 'forms_external_validate';

/**
 * Label for Done/Next that runs onFormDone → external validate.
 * While stamp is missing/stale, show Validate; otherwise keep fallback (Done/Next).
 */
export const getExternalApiFormActionTitleKey = (
  document,
  formStructure,
  formData,
  fallbackKey = 'title_done'
) => {
  if (
    getExternalApiValidationConfig(document) &&
    !isExternalApiValidationStampValid(document, formStructure, formData)
  ) {
    return FORMS_EXTERNAL_VALIDATE_TITLE_KEY;
  }
  return fallbackKey;
};

export const withExternalApiValidationStamp = (formDataMap, stamp) => {
  const next = new Map(formDataMap || []);
  if (stamp) {
    next.set(EXTERNAL_API_VALIDATION_STAMP_KEY, stamp);
  } else {
    next.delete(EXTERNAL_API_VALIDATION_STAMP_KEY);
  }
  return next;
};

export const clearExternalApiValidationStamp = formDataMap =>
  withExternalApiValidationStamp(formDataMap, null);

export const isFormItemInExternalApiValidation = (document, item) => {
  const validation = getExternalApiValidationConfig(document);
  if (!validation || !item?.customId) return false;
  return Object.values(validation).some(
    customIds => Array.isArray(customIds) && customIds.includes(item.customId)
  );
};

/**
 * Map API result.customId errors onto form item ids.
 */
export const mapExternalApiResultToFieldErrors = (result, formStructure) => {
  const fieldErrors = new Map();
  if (!result || typeof result !== 'object') return fieldErrors;

  Object.entries(result).forEach(([customId, errMessage]) => {
    if (!errMessage) return;
    const item = findFormItemByCustomId(formStructure, customId);
    if (item) {
      fieldErrors.set(item.id, {
        errMessage,
        element: item
      });
    }
  });
  return fieldErrors;
};

/**
 * Run external-api/validate for one document's configured endpoints.
 * @returns {Promise<{ok: boolean, skipped?: boolean, apiFailure?: boolean, fieldErrors: Map, stamp: object|null}>}
 */
export const runExternalApiValidation = async ({
  document,
  formStructure,
  formData,
  recipientRequestId,
  templateMode,
  validateFn,
  onEndpointStart
}) => {
  const validation = getExternalApiValidationConfig(document);
  if (!validation) {
    return {
      ok: true,
      skipped: true,
      fieldErrors: new Map(),
      stamp: null
    };
  }

  const PaperflyAPI =
    !validateFn && require('../../server/api/PaperflyAPI').default;
  const validate =
    validateFn || PaperflyAPI.validateWithExternalAPI.bind(PaperflyAPI);

  const requests = Object.entries(validation).map(
    ([externalApiId, customIds]) => {
      const data = buildExternalApiValidationPayload(
        formStructure,
        formData,
        customIds
      );
      onEndpointStart?.(externalApiId, customIds);
      return {
        externalApiId,
        promise: validate(externalApiId, recipientRequestId, data, templateMode)
      };
    }
  );

  const settled = await Promise.allSettled(requests.map(r => r.promise));
  const fieldErrors = new Map();
  let hasApiFailure = false;

  settled.forEach(result => {
    if (result.status === 'rejected') {
      hasApiFailure = true;
      return;
    }
    const response = result.value;
    if (!response || typeof response?.result !== 'object') {
      hasApiFailure = true;
      return;
    }
    mapExternalApiResultToFieldErrors(
      response.result,
      formStructure
    ).forEach((error, itemId) => fieldErrors.set(itemId, error));
  });

  if (hasApiFailure) {
    return {
      ok: false,
      apiFailure: true,
      fieldErrors,
      stamp: null
    };
  }

  if (fieldErrors.size) {
    return {
      ok: false,
      apiFailure: false,
      fieldErrors,
      stamp: null
    };
  }

  return {
    ok: true,
    fieldErrors,
    stamp: computeExternalApiValidationStamp(document, formStructure, formData)
  };
};
